import React, { FC, useCallback, useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { Grid, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { toast } from 'react-toastify'
import { useHistory, useLocation } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { endOfDay, startOfDay } from 'date-fns'
import { routes } from '../../../constants/routes'
import { icons } from '../../../constants/icons'
import { columns } from './adminUsersList'
import { DataPicker } from '../../../components/DatePicker'
import { Search } from '../../../components/Search'
import { GreenButton } from '../../../components/Buttons/GreenButton'
import { TableComponent } from '../../../components/Table'
import { useRest } from '../../../components/Context'
import useDebounce from '../../../hooks/useDebounce'
import { generateParams } from '../../../utils'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    width: '100%',
    paddingTop: theme.spacing(4),
    '& p': {
      display: 'none',
    },
  },
  rightSide: {
    flexWrap: 'nowrap',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: 'max-content',
    paddingTop: theme.spacing(2),
    '& :first-child': {
      marginLeft: 0,
    },
  },
  title: {
    paddingTop: theme.spacing(2),
  },
  button: {
    paddingLeft: 15,
  },
  table: {
    paddingTop: theme.spacing(3),
  },
}))

export const AdminUsers: FC = () => {
  const classes = useStyles()
  const { rxios } = useRest()
  const history = useHistory()

  const formMethods = useForm({
    mode: 'onBlur',
  })
  const { watch, getValues } = formMethods

  // eslint-disable-next-line
  const [loading, setLoading] = useState<boolean>(false)
  // eslint-disable-next-line
  const [isInitDate, setInitDate] = useState<boolean>(false)
  // eslint-disable-next-line
  const [values, setValues] = useState<any>({})
  // eslint-disable-next-line
  const [startDateSelected, setStartDateSelected] = useState<any>('')
  // eslint-disable-next-line
  const [endDateSelected, setEndDateSelected] = useState<any>('')
  const [queryString, setQueryString] = useState<string>('')
  const [searchDebounce, setSearchDebounce] = useState<string>('')
  const debouncedValue = useDebounce<string>(queryString, 200)

  const { search } = useLocation()
  const stringParams = new URLSearchParams(search)
  const pageUrl: number | string | null = stringParams.get('page')
  // eslint-disable-next-line
  useEffect(() => setSearchDebounce(queryString), [debouncedValue])

  const pushParamsToURL = useCallback(
    (startDate, endDate, searchString: string) => {
      const currentUrlParams = new URLSearchParams(window.location.search)

      if (startDate) {
        currentUrlParams.set('startDate', startDate)
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      } else {
        currentUrlParams.delete('startDate')
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      }

      if (endDate) {
        currentUrlParams.set('endDate', endDate)
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      } else {
        currentUrlParams.delete('endDate')
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      }

      if (searchString) {
        currentUrlParams.set('search', searchString)
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      } else {
        currentUrlParams.delete('search')
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      }
    },
    [history],
  )

  const loadData = useCallback(
    async (page, startDate = '', endDate = '', searchString = '') => {
      let cleanupFunction = false
      setLoading(true)

      const searchSplit: string[] = searchString.split(' ')
      let userRolesSearch = searchString.trim().toUpperCase()
      if (searchSplit.length === 2) {
        userRolesSearch = userRolesSearch.replace(/\s/, '_')
      }

      rxios
        .get(`crm-users?${generateParams({ page, count: '10', startDate, endDate, search: userRolesSearch })}`)
        .subscribe(
          // eslint-disable-next-line
          (next: any) => {
            setValues(next)
            history.push(`admin-users?page=${page}`)

            pushParamsToURL(startDate, endDate, searchString)

            if (!cleanupFunction) setLoading(false)
          },
          (error) => {
            pushParamsToURL(startDate, endDate, searchString)
            toast.error(error)
            setLoading(false)
          },
          () => {
            setLoading(false)
          },
        )
      cleanupFunction = true
    },
    // eslint-disable-next-line
    [history, rxios],
  )

  const initData = useCallback(
    async (page) => {
      let cleanupFunction = false
      setLoading(true)
      rxios.get(`crm-users?page=${page}&count=10`).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          setValues(next)
          if (!cleanupFunction) setLoading(false)
          setInitDate(true)
        },
        (error) => {
          toast.error(error)
          setLoading(false)
        },
        () => {
          setLoading(false)
        },
      )
      cleanupFunction = true
    },
    // eslint-disable-next-line
    [rxios],
  )

  useEffect(() => {
    ;(async () => {
      await initData(pageUrl || '1')
      history.push(`?page=${pageUrl || '1'}`)
    })()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'startDate') setStartDateSelected(value?.startDate?.toISOString())
      if (name === 'endDate') setEndDateSelected(value?.endDate?.toISOString())
      if (name === 'search') setQueryString(value?.[name])
    })
    return () => subscription.unsubscribe()
  }, [watch])

  useEffect(() => {
    if (isInitDate) {
      setEndDateSelected(endDateSelected === '1970-01-01' ? '' : endDateSelected)
      ;(async () => {
        await loadData(1, startDateSelected || '', endDateSelected || '', searchDebounce || '')
      })()
    }
    // eslint-disable-next-line
  }, [startDateSelected, endDateSelected, searchDebounce])

  return (
    <Grid container className={classes.topWrap}>
      <Typography variant="h1" className={classes.title}>
        Admin Users
      </Typography>
      <FormProvider {...formMethods}>
        <Grid container className={classes.rightSide}>
          <DataPicker
            placeholder={'Start Date'}
            margin={'0 8px'}
            dataPickerName={'startDate'}
            required={false}
            maxDate={getValues().endDate}
            formatValue={startOfDay}
          />
          <DataPicker
            placeholder={'End Date'}
            margin={'0 8px'}
            dataPickerName={'endDate'}
            required={false}
            minDate={getValues().startDate}
            formatValue={endOfDay}
          />
          <Search inputName={'search'} />
          <GreenButton
            width={120}
            prefix={icons.ADD}
            ml={8}
            text={'Add User'}
            href={routes.SETTINGS_ADMIN_USERS_CREATE}
          />
        </Grid>
      </FormProvider>

      <Grid container className={classes.table}>
        <TableComponent
          columns={columns}
          data={values.items}
          params={values.meta}
          // eslint-disable-next-line
          loadData={(pageNumber: any) =>
            loadData(pageNumber, startDateSelected || '', endDateSelected || '', searchDebounce || '')
          }
          loading={loading}
          twoAction
        />
      </Grid>
    </Grid>
  )
}
